/**
 * One cadence module (ORDR-119, FR-CFM-004), collapsed until asked for.
 *
 * OPENING IT IS WHAT FETCHES ITS DOMAINS, and only its domains. An entry that
 * is not a `ConfigDomain` member is listed with its own label and never
 * fetched: the GET would answer it with 400.
 */

import type { ReactElement } from "react";
import { keysOf, type CadenceModule } from "./cadence.js";
import { GroupCard } from "./GroupCard.js";
import type { ConfigScope } from "./index.js";
import type { Groups } from "./useGroups.js";

export interface CadenceSectionProps {
  readonly module: CadenceModule;
  readonly groups: Groups;
  readonly scope: ConfigScope;
  readonly onOpen: (module: CadenceModule) => void;
}

export function CadenceSection({ module, groups, scope, onOpen }: CadenceSectionProps): ReactElement {
  return (
    <details data-testid={`module-${module.key}`} className="rounded border border-[var(--color-neutral-300)]">
      <summary onClick={() => onOpen(module)} className="cursor-pointer p-2 text-sm">
        <span className="font-medium">{module.title}</span> <span className="text-xs">({module.whoAndWhen})</span>
      </summary>
      <ul className="flex flex-col gap-2 p-2">
        {module.entries.map((entry, index) => {
          if (entry.domain === null) {
            return (
              <li key={`${entry.label}-${String(index)}`} data-testid={`homeless-${module.key}-${entry.label}`} className="text-sm">
                <span className="font-medium">{entry.label}</span>: Not configurable here yet: ordering&apos;s
                configuration has no {entry.label} group.{entry.note !== undefined && ` ${entry.note}`}
              </li>
            );
          }
          const domain = entry.domain;
          return (
            <GroupCard
              key={domain}
              testId={`group-${module.key}-${domain}`}
              label={entry.label}
              domain={domain}
              state={groups.states[domain]}
              scope={scope}
              api={groups.api}
              keys={keysOf(entry)}
              onRetry={() => groups.reload(domain)}
              onSaved={(reading) => groups.adopt(domain, reading)}
            />
          );
        })}
      </ul>
    </details>
  );
}
